import { useAuth } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { t, Trans } from "@lingui/macro";
import { completeProfileSchema } from "@reactive-resume/dto";
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { LoaderCircle } from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { type Control, useForm } from "react-hook-form";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { getFirstTouch } from "@/client/analytics/attribution";
import { setPersonOnce } from "@/client/analytics/mixpanel";
import { trackEvent } from "@/client/analytics/track";
import { planIntentInPath } from "@/client/components/plan-dialog/plan-intent";
import { useCompleteProfile } from "@/client/services/auth";
import { isIdentityLinkConflict, useUser } from "@/client/services/user";
import { firstRoundLegalUrl } from "@/client/utils/firstRoundAppUrl";

import { useIsFirstRoundBrand } from "../auth-brand";

type FormValues = z.infer<typeof completeProfileSchema>;

// First-touch UTMs and landing page for the lead record.
const leadAttribution = () => {
  const touch = getFirstTouch();
  if (!touch) return {};
  return {
    utmSource: touch.utm_source,
    utmMedium: touch.utm_medium,
    utmCampaign: touch.utm_campaign,
    landingPage:
      touch.landing_page === undefined
        ? undefined
        : `${touch.landing_host ?? ""}${touch.landing_page}`.slice(0, 200),
  };
};

/**
 * Where a sign-up came from, for signup_completed and the profile: the demo results gate, a plan or
 * code link from firstround.com.au, otherwise the site the visitor first landed on.
 */
export const signupEntry = (redirectTo: string) => {
  if (redirectTo.startsWith("/demo/practice/")) return "demo_results";
  if (planIntentInPath(redirectTo)) return "plan_link";
  const host = getFirstTouch()?.landing_host ?? "";
  if (host.includes("firstround")) return "first_round";
  if (host) return getFirstTouch()?.landing_page === "/" ? "hub" : "theresume";
  return "direct";
};

const LEGAL_LINK_CLASS = "font-medium text-sky-600 underline";

const ConsentCheckbox = ({
  name,
  control,
  label,
}: {
  name: "acceptTerms";
  control: Control<FormValues>;
  label: React.ReactNode;
}) => (
  <FormField
    name={name}
    control={control}
    render={({ field }) => (
      <FormItem className="flex min-h-14 flex-row items-start gap-3 py-3.5">
        <FormControl>
          <Checkbox
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            className="relative top-0.5 shrink-0"
          />
        </FormControl>
        <div className="space-y-1 leading-none">
          <FormLabel className="cursor-pointer text-sm font-normal leading-relaxed text-slate-700">
            {label}
          </FormLabel>
          <FormMessage />
        </div>
      </FormItem>
    )}
  />
);

export const CompleteProfilePage = () => {
  const { completeProfile, loading } = useCompleteProfile();
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const { user, loading: userLoading, error: userError } = useUser();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  // Every new user becomes a First Round lead; coming through First Round only changes the source
  // and the checkbox wording.
  const isFirstRound = useIsFirstRoundBrand();

  const form = useForm<FormValues>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      name: "",
      username: "",
      locale: "en-US",
      acceptTerms: false,
    },
  });

  // Prefill from the JIT-provisioned user (name/username derived from Clerk).
  useEffect(() => {
    if (!user) return;
    if (!form.getValues("name")) form.setValue("name", user.name);
    if (!form.getValues("username")) form.setValue("username", user.username);
  }, [user, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      await completeProfile({
        ...data,
        ...(isFirstRound ? { firstRound: true } : {}),
        ...leadAttribution(),
      });
      // First-time Clerk sign-ups land here exactly once, so this is the signup moment.
      const entry = signupEntry(redirectTo);
      const intent = planIntentInPath(redirectTo);
      setPersonOnce({ signup_entry: entry });
      trackEvent("signup_completed", {
        method: user?.provider,
        entry,
        has_code: Boolean(intent?.code),
        plan_intent: intent?.plan,
      });
    } catch (error) {
      form.reset();
    }
  };

  if (userLoading) return null;

  if (isIdentityLinkConflict(userError)) {
    return <Navigate to="/auth/account-link-conflict" replace />;
  }

  // Just signed up: Clerk has the session but the app's user record is still on its way. Wait for
  // it instead of sending a brand-new user back to sign-in.
  if (!user && (!clerkLoaded || (isSignedIn && !userError))) return null;

  // Not signed in via Clerk (or provisioning failed) — nothing to complete here.
  if (!user) return <Navigate to="/auth/login" replace />;

  // Profile already completed — this page is only for first-time Clerk sign-ups.
  if (user.profileCompletedAt) return <Navigate to={redirectTo} replace />;

  return (
    <div className="space-y-7">
      <Helmet>
        <title>
          {t`Complete your profile`} - {t`theResume`}
        </title>
      </Helmet>

      <div>
        <p className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-sky-600">
          {t`One last step`}
        </p>
        <h2 className="text-[2rem] font-semibold leading-[1.12] -tracking-wide text-slate-950 [text-wrap:balance] sm:text-[2.25rem]">
          {t`Complete your profile`}
        </h2>
        <p className="mt-3 max-w-[48ch] text-base leading-relaxed text-slate-500">
          {t`Confirm your details and review how we use your data.`}
        </p>
      </div>

      <Form {...form}>
        <form className="flex flex-col gap-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-slate-700">{t`Name`}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t`John Doe`}
                    autoComplete="name"
                    className="h-12 rounded-xl border-slate-200 bg-[oklch(0.995_0.004_235)] focus-visible:border-sky-500 focus-visible:ring-4 focus-visible:ring-sky-500/10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="username"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-slate-700">{t`Username`}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t`john.doe`}
                    autoComplete="username"
                    className="h-12 rounded-xl border-slate-200 bg-[oklch(0.995_0.004_235)] focus-visible:border-sky-500 focus-visible:ring-4 focus-visible:ring-sky-500/10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <fieldset className="mt-1">
            <legend className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {t`Consent and acknowledgements`}
            </legend>

            <div className="divide-y divide-slate-200 border-y border-slate-200">
              <ConsentCheckbox
                name="acceptTerms"
                control={form.control}
                label={
                  isFirstRound ? (
                    <Trans>
                      I agree to the First Round{" "}
                      <a
                        href={firstRoundLegalUrl("/privacy")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={LEGAL_LINK_CLASS}
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href={firstRoundLegalUrl("/terms")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={LEGAL_LINK_CLASS}
                      >
                        Terms of Service
                      </a>
                      , including AI processing of my resume and conversation transcripts and
                      occasional emails from First Round. We never send spam, and you can
                      unsubscribe at any time.
                    </Trans>
                  ) : (
                    <Trans>
                      I agree to the{" "}
                      <Link to="/privacy" target="_blank" className={LEGAL_LINK_CLASS}>
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link to="/terms" target="_blank" className={LEGAL_LINK_CLASS}>
                        Terms of Service
                      </Link>
                      , including AI processing of my resume and conversation transcripts and
                      occasional emails from theResume and First Round, our interview practice
                      service. We never send spam, and you can unsubscribe at any time.
                    </Trans>
                  )
                }
              />
            </div>
          </fieldset>

          <Button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            className="mt-1 h-12 w-full rounded-full bg-sky-600 font-semibold text-white shadow-[0_8px_24px_oklch(0.63_0.17_235/0.22)] transition duration-200 hover:bg-sky-700 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                <Trans>Creating your account...</Trans>
              </span>
            ) : (
              <Trans>Create my account</Trans>
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
